import { useRef } from "react";

import { cn } from "@/lib/utils";

interface OtpSlotsProps {
  length: number;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  error?: boolean;
}

/**
 * Length-agnostic OTP slot input. One box per digit; auto-advance on entry,
 * backspace walks to the previous slot, and a paste anywhere redistributes
 * digits across the slots. Slot 0 carries autoComplete="one-time-code" so
 * mobile keyboards offer the code from the user's inbox.
 */
export function OtpSlots({
  length,
  value,
  onChange,
  disabled = false,
  autoFocus = true,
  error = false,
}: OtpSlotsProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const focusSlot = (index: number) => {
    const target = inputsRef.current[Math.max(0, Math.min(index, length - 1))];
    target?.focus();
    target?.select();
  };

  const handleChange = (index: number, incoming: string) => {
    const digits = incoming.replace(/\D/g, "");
    if (!digits) {
      // Non-digit input into a slot: clear it
      onChange(value.slice(0, index) + value.slice(index + 1));
      return;
    }
    // Redistribute from this slot forward (single keystroke and paste both land here)
    const next = (value.slice(0, index) + digits).slice(0, length);
    onChange(next);
    focusSlot(next.length >= length ? length - 1 : next.length);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (value[index]) {
        onChange(value.slice(0, index) + value.slice(index + 1));
      } else if (index > 0) {
        onChange(value.slice(0, index - 1) + value.slice(index));
        focusSlot(index - 1);
      }
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusSlot(index - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusSlot(index + 1);
    }
  };

  return (
    <div className="flex justify-center gap-2" role="group" aria-label="One-time code">
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => (inputsRef.current[i] = el)}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          maxLength={length}
          autoComplete={i === 0 ? "one-time-code" : "off"}
          autoFocus={autoFocus && i === 0}
          disabled={disabled}
          value={value[i] ?? ""}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
          aria-label={`Digit ${i + 1} of ${length}`}
          className={cn(
            "h-12 w-10 rounded-md border bg-background text-center font-mono text-xl",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:border-input",
            "disabled:cursor-not-allowed disabled:opacity-50",
            error ? "border-red-500" : "border-input"
          )}
        />
      ))}
    </div>
  );
}
